import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect } from 'preact/hooks';
import { RefreshCw, Trash2, AlertCircle } from 'lucide-preact';
import { useCompany } from '@/hooks/useCompany';
import { formSubmissionsApi } from '@/api/formSubmissions';
import { FailedSubmissionsTable } from './errorQueue/FailedSubmissionsTable';
export function SettingsErrorQueue() {
    const { currentWorkspaceId } = useCompany();
    const [activeTab, setActiveTab] = useState('reprocessable');
    const [loading, setLoading] = useState(false);
    const [submissions, setSubmissions] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [actionInProgress, setActionInProgress] = useState(false);
    // Date range for non-reprocessable tab (default to 30 days ago)
    const [sinceDate, setSinceDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    });
    useEffect(() => {
        if (currentWorkspaceId) {
            loadSubmissions();
        }
    }, [activeTab, currentWorkspaceId, sinceDate]);
    const loadSubmissions = async () => {
        if (!currentWorkspaceId) {
            setError('No workspace selected');
            return;
        }
        setLoading(true);
        setError(null);
        setSelectedIds([]);
        try {
            let response;
            if (activeTab === 'reprocessable') {
                response = await formSubmissionsApi.getReprocessableFailedSubmissions(currentWorkspaceId);
            }
            else {
                // Convert date to ISO UTC string
                const sinceUtc = new Date(sinceDate).toISOString();
                response = await formSubmissionsApi.getNonReprocessableFailedSubmissions(currentWorkspaceId, sinceUtc);
            }
            if (response.error || !response.data) {
                setError(response.error || 'Failed to load submissions');
                setSubmissions([]);
            }
            else {
                setSubmissions(response.data);
            }
        }
        catch (err) {
            setError('Failed to load submissions');
            setSubmissions([]);
        }
        finally {
            setLoading(false);
        }
    };
    const handleRetry = async () => {
        if (selectedIds.length === 0) {
            setError('Please select at least one submission to retry');
            return;
        }
        if (!currentWorkspaceId) {
            setError('No workspace selected');
            return;
        }
        setActionInProgress(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const response = await formSubmissionsApi.retryFailedSubmissions(currentWorkspaceId, selectedIds);
            if (response.error) {
                setError(response.error);
            }
            else {
                setSuccessMessage(`Successfully initiated retry for ${selectedIds.length} submission(s). Please check back in a few minutes to see the results.`);
                setSelectedIds([]);
                // Reload the list after a short delay
                setTimeout(() => {
                    loadSubmissions();
                }, 1000);
            }
        }
        catch (err) {
            setError('Failed to retry submissions');
        }
        finally {
            setActionInProgress(false);
        }
    };
    const handleDelete = async () => {
        if (selectedIds.length === 0) {
            setError('Please select at least one submission to delete');
            return;
        }
        if (!currentWorkspaceId) {
            setError('No workspace selected');
            return;
        }
        if (!confirm(`Are you sure you want to delete ${selectedIds.length} submission(s)? This action cannot be undone.`)) {
            return;
        }
        setActionInProgress(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const response = await formSubmissionsApi.deleteFailedSubmissions(currentWorkspaceId, selectedIds);
            if (response.error) {
                setError(response.error);
            }
            else {
                setSuccessMessage(`Successfully deleted ${selectedIds.length} submission(s)`);
                setSelectedIds([]);
                // Reload the list
                setTimeout(() => {
                    loadSubmissions();
                }, 1000);
            }
        }
        catch (err) {
            setError('Failed to delete submissions');
        }
        finally {
            setActionInProgress(false);
        }
    };
    const handlePayloadUpdate = async (submissionId, newPayload) => {
        if (!currentWorkspaceId) {
            setError('No workspace selected');
            throw new Error('No workspace selected');
        }
        try {
            const response = await formSubmissionsApi.updateFailedSubmissionPayload(currentWorkspaceId, submissionId, newPayload);
            if (response.error) {
                setError(response.error);
                throw new Error(response.error);
            }
            else {
                setSuccessMessage('Payload updated successfully. Retrying submission...');
                // Reload the list after a short delay
                setTimeout(() => {
                    loadSubmissions();
                }, 1000);
            }
        }
        catch (err) {
            const error = err;
            setError(error.message || 'Failed to update payload');
            throw err;
        }
    };
    const handleDateChange = (e) => {
        const target = e.target;
        setSinceDate(target.value);
    };
    if (!currentWorkspaceId) {
        return (_jsx("div", { className: "p-6 max-w-7xl mx-auto", children: _jsxs("div", { className: "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4", children: [_jsx("div", { className: "text-yellow-700 dark:text-yellow-400 font-medium mb-1", children: "No Workspace Available" }), _jsx("div", { className: "text-sm text-yellow-600 dark:text-yellow-300", children: "You need to be assigned to at least one workspace to view error queue." })] }) }));
    }
    return (_jsxs("div", { className: "p-6 max-w-7xl mx-auto", children: [_jsxs("div", { className: "mb-6", children: [_jsx("h1", { className: "text-2xl font-bold text-slate-900 dark:text-white mb-2", children: "Workspace Error Queue" }), _jsx("p", { className: "text-slate-600 dark:text-slate-300", children: "Manage and reprocess failed form submissions" })] }), _jsx("div", { className: "mb-6 border-b border-slate-200 dark:border-slate-700", children: _jsxs("div", { className: "flex gap-4", children: [_jsx("button", { onClick: () => setActiveTab('reprocessable'), className: `px-4 py-2 font-medium transition-colors border-b-2 ${activeTab === 'reprocessable'
                                ? 'border-green-600 text-green-600 dark:text-green-400'
                                : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`, children: "Reprocessable (Transient Failures)" }), _jsx("button", { onClick: () => setActiveTab('non-reprocessable'), className: `px-4 py-2 font-medium transition-colors border-b-2 ${activeTab === 'non-reprocessable'
                                ? 'border-green-600 text-green-600 dark:text-green-400'
                                : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`, children: "Non-Reprocessable" })] }) }), activeTab === 'non-reprocessable' && (_jsxs("div", { className: "mb-4 flex items-center gap-4", children: [_jsx("label", { className: "text-sm font-medium text-slate-700 dark:text-slate-300", children: "Show failures since:" }), _jsx("input", { type: "date", value: sinceDate, onChange: handleDateChange, className: "px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500" })] })), error && (_jsx("div", { className: "mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(AlertCircle, { className: "w-5 h-5 text-red-600 dark:text-red-400" }), _jsx("div", { className: "text-red-700 dark:text-red-400 font-medium", children: error })] }) })), successMessage && (_jsx("div", { className: "mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4", children: _jsx("div", { className: "text-green-700 dark:text-green-400 font-medium", children: successMessage }) })), _jsxs("div", { className: "mb-4 flex gap-3", children: [_jsxs("button", { onClick: handleRetry, disabled: selectedIds.length === 0 || actionInProgress, className: "flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed", children: [_jsx(RefreshCw, { className: `w-4 h-4 ${actionInProgress ? 'animate-spin' : ''}` }), "Retry Selected (", selectedIds.length, ")"] }), _jsxs("button", { onClick: handleDelete, disabled: selectedIds.length === 0 || actionInProgress, className: "flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed", children: [_jsx(Trash2, { className: "w-4 h-4" }), "Delete Selected (", selectedIds.length, ")"] }), _jsxs("button", { onClick: loadSubmissions, disabled: loading || actionInProgress, className: "flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed", children: [_jsx(RefreshCw, { className: `w-4 h-4 ${loading ? 'animate-spin' : ''}` }), "Refresh"] })] }), _jsx("div", { className: "bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700", children: _jsx(FailedSubmissionsTable, { submissions: submissions, selectedIds: selectedIds, onSelectionChange: setSelectedIds, onPayloadUpdate: handlePayloadUpdate, loading: loading }) })] }));
}
